import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { SpatialRun } from "src/pages/runs/types";
import React, { useEffect, useMemo } from "react";
import { useAuth } from "src/contexts/auth";
import { fetchWithToken } from "src/hooks/useFetch";
import FlowcellTileSelector from "./FlowcellTileSelector";
import {
  telemetryInstrumentFolder,
  telemetryLogsRootFrom,
} from "src/utils/telemetry-logs-path";
import TileInfo1Well from "./tile-layouts/TileInfo1Well.json";
import TileInfo12Well from "./tile-layouts/TileInfo12Well.json";
import TileInfo48Well from "./tile-layouts/TileInfo48Well.json";
import {
  isSingleWellLayout,
  KIT_TYPE_TISSUE,
  WELL_LAYOUT_PANOS_SLIDE,
} from "./well-layout";

const bucket = "elembio-prod-usw2-s3-aviti-telemetry-logs-usw2";

interface Tile {
  Name: string;
  XMillimeters: number;
  YMillimeters: number;
}

interface Well {
  WellLocation: string;
  Tiles: Tile[];
}

interface TileLayout {
  Wells: Well[];
}

const EXPORT_WIDTH = 3840;
const EXPORT_HEIGHT = 2160;

const getTileLayoutData = (wellLayout: string): TileLayout => {
  if (isSingleWellLayout(wellLayout)) return TileInfo1Well as TileLayout;
  if (wellLayout === "FortyEightWell") return TileInfo48Well as TileLayout;
  return TileInfo12Well as TileLayout;
};

const sanitizeFileName = (value: string): string => {
  const cleaned = value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || "run";
};

const getThumbnailLoadErrorMessage = (error: unknown): string => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : "";
  if (/404|not found/i.test(message)) {
    return "Thumbnail not found.";
  }
  return "Failed to load thumbnail.";
};

export default function CytoRunThumbnail({ run }: { run: SpatialRun }) {
  const [selectedWell, setSelectedWell] = React.useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth();
  const [thumbnailURLs, setThumbnailURLs] = React.useState<
    Record<string, string>
  >({});
  const [thumbnailLoadState, setThumbnailLoadState] = React.useState<
    Record<
      string,
      { loading: boolean; error: string | null; attempted: boolean }
    >
  >({});
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportErrorMessage, setExportErrorMessage] = React.useState<
    string | null
  >(null);
  const {
    tenantID,
    instrumentID,
    instrumentSerialNumber,
    instrumentName,
    runID,
    runName,
    wellLayout,
  } = run || {};

  const prefix = useMemo(() => {
    let tID = tenantID;
    if (tID === "") tID = "ten_traefikee";
    return `${bucket}/${telemetryLogsRootFrom(run)}/${tID}/${telemetryInstrumentFolder(instrumentID, instrumentSerialNumber, instrumentName, run?.model || run?.instrumentModel)}${
      runID ? `/runs/${runID}/Thumbnails/Well` : ""
    }`;
  }, [run, tenantID, instrumentID, instrumentSerialNumber, instrumentName, runID]);
  const postfix = "_Thumbnail.png";

  // Determine the layout type for FlowcellTileSelector
  const layoutType = useMemo(() => {
    if (wellLayout) return wellLayout;
    // Fallback based on well count. Only reached for runs with no stored
    // wellLayout. Tissue kits only run on single-well flow cells and default to
    // PanosSlide, so they must never fall through to the 12-well default. Any
    // other single-well run is a OneWell (Mega Well); PanosSlide is not valid
    // for non-tissue kits.
    const wellCount = run?.wells?.length || 0;
    if (run?.kitType === KIT_TYPE_TISSUE) return WELL_LAYOUT_PANOS_SLIDE;
    if (wellCount === 1) return "OneWell";
    if (wellCount === 48) return "FortyEightWell";
    return "TwelveWellStandard";
  }, [wellLayout, run?.wells?.length, run?.kitType]);

  useEffect(() => {
    setSelectedWell(null);
    setThumbnailURLs({});
    setThumbnailLoadState({});
    setExportErrorMessage(null);
  }, [runID]);

  useEffect(() => {
    let isCancelled = false;

    run?.wells.forEach((well) => {
      setThumbnailLoadState((prev) => ({
        ...prev,
        [well.WellLocation]: { loading: true, error: null, attempted: true },
      }));
      const payload = { path: prefix + well.WellLocation + postfix };
      fetchWithToken(getAccessTokenSilently, "/thumbnailURL", {
        method: "POST",
        body: JSON.stringify(payload),
      })
        .then((res) => {
          if (isCancelled) return;
          console.log("url", res);
          if (!res || typeof res !== "string") {
            setThumbnailLoadState((prev) => ({
              ...prev,
              [well.WellLocation]: {
                loading: false,
                error: "No thumbnail available for this well.",
                attempted: true,
              },
            }));
            return;
          }
          setThumbnailURLs((prev) => ({
            ...prev,
            [well.WellLocation]: res as string,
          }));
          setThumbnailLoadState((prev) => ({
            ...prev,
            [well.WellLocation]: {
              loading: false,
              error: null,
              attempted: true,
            },
          }));
        })
        .catch((error) => {
          if (isCancelled) return;
          console.error(error);
          setThumbnailLoadState((prev) => ({
            ...prev,
            [well.WellLocation]: {
              loading: false,
              error: getThumbnailLoadErrorMessage(error),
              attempted: true,
            },
          }));
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [prefix, run?.wells, getAccessTokenSilently]);

  const handleWellSelect = (wellLocation: string) => {
    setSelectedWell(wellLocation);
  };

  const selectedWellState = selectedWell
    ? thumbnailLoadState[selectedWell]
    : undefined;
  const selectedWellImage = selectedWell
    ? thumbnailURLs[selectedWell]
    : undefined;

  const handleOpenThumbnailInNewWindow = () => {
    if (!selectedWellImage) return;
    window.open(selectedWellImage, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPng = async () => {
    if (isExporting) return;
    setExportErrorMessage(null);

    const layoutData = getTileLayoutData(layoutType);
    const wellsWithImages = layoutData.Wells.filter(
      (well) => !!thumbnailURLs[well.WellLocation]
    );
    if (wellsWithImages.length === 0) {
      setExportErrorMessage("No thumbnails are available to export yet.");
      return;
    }

    setIsExporting(true);
    let bitmapResources: ImageBitmap[] = [];
    try {
      const loadedWells = await Promise.all(
        wellsWithImages.map(async (well) => {
          const avgX =
            well.Tiles.reduce((acc, tile) => acc + tile.XMillimeters, 0) /
            Math.max(well.Tiles.length, 1);
          const avgY =
            well.Tiles.reduce((acc, tile) => acc + tile.YMillimeters, 0) /
            Math.max(well.Tiles.length, 1);

          try {
            const payload = { path: `${prefix}${well.WellLocation}${postfix}` };
            const blob = (await fetchWithToken(
              getAccessTokenSilently,
              "/thumbnailBytes",
              {
                method: "POST",
                body: JSON.stringify(payload),
              }
            )) as Blob;
            const image = await createImageBitmap(blob);
            return { well, avgX, avgY, image };
          } catch (error) {
            console.error(error);
            return { well, avgX, avgY, image: null };
          }
        })
      );

      const drawableWells = loadedWells.filter(
        (entry): entry is typeof entry & { image: ImageBitmap } =>
          entry.image !== null
      );
      bitmapResources = drawableWells.map((entry) => entry.image);
      if (drawableWells.length === 0) {
        setExportErrorMessage(
          "Failed to load thumbnails from /thumbnailBytes. Please try again."
        );
        return;
      }

      // Force landscape flowcell orientation for export by rotating layout geometry.
      // We rotate coordinates only; thumbnail pixels remain unrotated.
      const exportEntries = drawableWells.map((entry) => ({
        ...entry,
        plotX: entry.avgY,
        plotY: -entry.avgX,
      }));

      const xs = exportEntries.map((entry) => entry.plotX);
      const ys = exportEntries.map((entry) => entry.plotY);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const canvas = document.createElement("canvas");
      canvas.width = EXPORT_WIDTH;
      canvas.height = EXPORT_HEIGHT;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setExportErrorMessage("Could not initialize export canvas.");
        return;
      }

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

      ctx.fillStyle = "#111111";
      ctx.font = "700 72px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(runName || "Run", EXPORT_WIDTH / 2, 64);

      const layoutLeft = 120;
      const layoutTop = 170;
      const layoutWidth = EXPORT_WIDTH - layoutLeft * 2;
      const layoutHeight = EXPORT_HEIGHT - layoutTop - 90;

      const minPositiveGap = (values: number[]) => {
        let minGap = Number.POSITIVE_INFINITY;
        for (let index = 1; index < values.length; index += 1) {
          const gap = values[index] - values[index - 1];
          if (gap > 1 && gap < minGap) minGap = gap;
        }
        return Number.isFinite(minGap) ? minGap : 0;
      };

      const xDataValues = Array.from(new Set(xs)).sort((a, b) => a - b);
      const yDataValues = Array.from(new Set(ys)).sort((a, b) => a - b);
      const gapXData = minPositiveGap(xDataValues);
      const gapYData = minPositiveGap(yDataValues);

      // Add half-cell padding in coordinate space so outer wells are visible.
      const xPaddingData = gapXData > 0 ? gapXData / 2 : 0.5;
      const yPaddingData = gapYData > 0 ? gapYData / 2 : 0.5;
      const xExtentMin = minX - xPaddingData;
      const xExtentMax = maxX + xPaddingData;
      const yExtentMin = minY - yPaddingData;
      const yExtentMax = maxY + yPaddingData;
      const extentRangeX = Math.max(xExtentMax - xExtentMin, 1);
      const extentRangeY = Math.max(yExtentMax - yExtentMin, 1);

      const geometryScale = Math.min(
        layoutWidth / extentRangeX,
        layoutHeight / extentRangeY
      );
      const scaledLayoutWidth = extentRangeX * geometryScale;
      const scaledLayoutHeight = extentRangeY * geometryScale;
      const centeredLeft = layoutLeft + (layoutWidth - scaledLayoutWidth) / 2;
      const centeredTop = layoutTop + (layoutHeight - scaledLayoutHeight) / 2;
      const isSingleWellExport = exportEntries.length === 1;

      const cellWidth = Math.max(
        gapXData * geometryScale,
        scaledLayoutWidth * 0.08
      );
      const cellHeight = Math.max(
        gapYData * geometryScale,
        scaledLayoutHeight * 0.25
      );
      const imageAreaWidth = isSingleWellExport
        ? Math.max(280, layoutWidth * 0.96)
        : Math.max(120, cellWidth * 0.93);
      const imageAreaHeight = isSingleWellExport
        ? Math.max(280, layoutHeight * 0.86)
        : Math.max(120, cellHeight * 0.78);

      const firstImage = drawableWells[0].image;
      const naturalWidth = firstImage.width;
      const naturalHeight = firstImage.height;
      const baseScale = Math.min(
        imageAreaWidth / naturalWidth,
        imageAreaHeight / naturalHeight
      );
      const drawWidth = naturalWidth * baseScale;
      const drawHeight = naturalHeight * baseScale;

      const wellLabelMap = new Map(
        (run?.wells || []).map((well) => [well.WellLocation, well.WellLabel])
      );

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const maxLabelWidth = Math.max(120, cellWidth * 0.9);

      const fitLabelText = (text: string) => {
        const maxFontSize = 30;
        const minFontSize = 16;
        let fontSize = maxFontSize;
        const fontWeight = 600;
        let displayText = text;

        const measure = (candidate: string, size: number) => {
          ctx.font = `${fontWeight} ${size}px Arial`;
          return ctx.measureText(candidate).width;
        };

        while (
          fontSize > minFontSize &&
          measure(displayText, fontSize) > maxLabelWidth
        ) {
          fontSize -= 1;
        }

        if (measure(displayText, fontSize) > maxLabelWidth) {
          let shortened = displayText;
          while (shortened.length > 0) {
            shortened = shortened.slice(0, -1);
            const candidate = `${shortened}...`;
            if (measure(candidate, fontSize) <= maxLabelWidth) {
              displayText = candidate;
              break;
            }
          }
        }

        return { fontSize, displayText };
      };

      exportEntries.forEach(({ well, plotX, plotY, image }) => {
        const centerX = centeredLeft + (plotX - xExtentMin) * geometryScale;
        const centerY = centeredTop + (plotY - yExtentMin) * geometryScale;

        ctx.strokeStyle = "#d0d0d0";
        ctx.lineWidth = 3;
        ctx.strokeRect(
          centerX - drawWidth / 2 - 4,
          centerY - drawHeight / 2 - 4,
          drawWidth + 8,
          drawHeight + 8
        );

        ctx.drawImage(
          image,
          centerX - drawWidth / 2,
          centerY - drawHeight / 2,
          drawWidth,
          drawHeight
        );

        const wellLabel = wellLabelMap.get(well.WellLocation);
        const labelText = wellLabel
          ? `${well.WellLocation} - ${wellLabel}`
          : well.WellLocation;
        ctx.fillStyle = "#1a1a1a";
        const fittedLabel = fitLabelText(labelText);
        ctx.font = `600 ${fittedLabel.fontSize}px Arial`;
        ctx.fillText(
          fittedLabel.displayText,
          centerX,
          centerY + drawHeight / 2 + fittedLabel.fontSize + 4
        );
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });

      if (!blob) {
        setExportErrorMessage("PNG export failed. Please try again.");
        return;
      }

      const fileName = `${sanitizeFileName(runName || "run")}_thumbnails.png`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export flowcell thumbnail PNG:", error);
      setExportErrorMessage("Failed to export PNG. Please try again.");
    } finally {
      bitmapResources.forEach((image) => image.close());
      setIsExporting(false);
    }
  };

  return (
    <Box display="flex" gap={2}>
      <FlowcellTileSelector
        wellLayout={layoutType}
        selectedWell={selectedWell}
        onWellSelect={handleWellSelect}
      />

      <Box display="flex" flexDirection="column" alignItems={"center"}>
        <Button
          variant="contained"
          size="small"
          onClick={handleDownloadPng}
          disabled={Object.keys(thumbnailURLs).length === 0 || isExporting}
          sx={{ mb: 2 }}
        >
          {isExporting ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} color="inherit" />
              Exporting PNG...
            </Box>
          ) : (
            "Download Full Flowcell PNG"
          )}
        </Button>
        <Typography variant="h6" fontWeight="bold" pb={2}>
          {selectedWell || "Select a well"}
        </Typography>
        <Box
          sx={{
            width: 560,
            height: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #ddd",
            borderRadius: 1,
            backgroundColor: "#fff",
            overflow: "hidden",
            p: 2,
          }}
        >
          {!selectedWell && (
            <Typography sx={{ color: "#777" }}>
              Select a well to view thumbnail
            </Typography>
          )}

          {selectedWell && selectedWellState?.loading && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={22} />
              <Typography sx={{ color: "#666" }}>
                Loading thumbnail...
              </Typography>
            </Box>
          )}

          {selectedWell &&
            !selectedWellState?.loading &&
            selectedWellState?.error && (
              <Typography color="error">{selectedWellState.error}</Typography>
            )}

          {selectedWell &&
            !selectedWellState?.loading &&
            !selectedWellState?.error &&
            !selectedWellImage &&
            selectedWellState?.attempted && (
              <Typography sx={{ color: "#777" }}>
                No thumbnail available for this well.
              </Typography>
            )}

          {selectedWell &&
            !selectedWellState?.loading &&
            !selectedWellState?.error &&
            selectedWellImage && (
              <img
                src={selectedWellImage}
                alt="thumbnail"
                onClick={handleOpenThumbnailInNewWindow}
                title="Open full-size image in a new window"
                style={{
                  transform: "rotate(-90deg)",
                  transformOrigin: "center center",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  display: "block",
                  cursor: "zoom-in",
                }}
              />
            )}
        </Box>
        {selectedWell &&
          !selectedWellState?.loading &&
          !selectedWellState?.error &&
          selectedWellImage && (
            <Typography
              variant="caption"
              sx={{ mt: 2, color: "#666", fontStyle: "italic" }}
            >
              Image rotated 90° CCW to match flow cell layout
            </Typography>
          )}
      </Box>
      <Snackbar
        open={!!exportErrorMessage}
        autoHideDuration={5000}
        onClose={() => setExportErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setExportErrorMessage(null)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {exportErrorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
